import React, { useState } from 'react';
import { login } from '../api/api';
import { useTranslation } from '../i18n';

interface DesktopLoginPageProps {
  onClose: () => void;
  onRegisterClick?: () => void;
  onLoginSuccess?: () => void;
}

const DesktopLoginPage: React.FC<DesktopLoginPageProps> = ({ onClose, onRegisterClick, onLoginSuccess }) => {
  const { t } = useTranslation();
  const [identity, setIdentity] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await login({ identity, password });
      if (onLoginSuccess) {
        onLoginSuccess();
      }
      onClose();
    } catch (err: any) {
      console.error('Login Error:', err);
      const isInvalidCreds = err.message?.toLowerCase().includes('invalid credentials') || err.message?.includes('401') || err.message === 'HTTP 401';
      setError(isInvalidCreds ? t('login.invalidCredentials') : err.message || t('login.failed') || 'Anmeldung fehlgeschlagen');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-[#f4f4f4] z-[200] flex flex-col overflow-y-auto">
      {/* Header */}
      <header className="sticky top-0 z-[100] shadow-md text-white h-[56px] flex-shrink-0 flex items-center px-6" style={{ backgroundImage: 'var(--bg-brand-gradient-vertical)' }}>
        <div className="w-full mx-auto flex items-center gap-10">
          <button onClick={onClose} className="header-alex-logo hover:opacity-80 transition cursor-pointer flex items-center">
            <img src="/tip24beth.svg" alt="Tip24" className="h-8 object-contain" />
          </button>
          <nav className="flex gap-1 h-full">
            {[
              { id: 'home', label: 'Sportwetten' },
              { id: 'live', label: 'Livewetten' },
              { id: 'games', label: 'Games' },
              { id: 'aktionen', label: 'Aktionen' },
            ].map((item) => (
              <button
                key={item.id}
                onClick={onClose}
                className="text-[13px] font-bold h-[56px] px-4 transition uppercase tracking-wider flex items-center text-white/80 border-b-4 border-transparent hover:text-white hover:bg-tipico-red-dark"
              >
                {item.label}
              </button>
            ))}
          </nav>
          <div className="ml-auto flex items-center gap-4">
            <button type="button" className="text-white font-bold text-[15px] border-b-2 border-white py-1">
              Einloggen
            </button>
            <button 
              type="button" 
              onClick={onRegisterClick}
              className="bg-white text-tipico-red px-6 py-2 rounded-full text-[15px] font-bold shadow-md hover:bg-gray-100 transition"
            >
              Registrieren
            </button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1 flex items-center justify-center py-10 px-4">
        <div className="bg-white w-full max-w-[960px] rounded-lg shadow-2xl overflow-hidden flex min-h-[740px]">
          
          {/* Left Side: Hero Image */}
          <div className="hidden md:flex md:w-[45%] relative overflow-hidden">
            <img 
              src="/images/login-bg.jpg" 
              alt="Login Hero" 
              className="absolute inset-0 w-full h-full object-cover rounded-[4px]"
            />
            <div className="absolute inset-0 bg-black/40 flex flex-col justify-end p-10 pb-20">
              <h2 className="text-white text-[32px] md:text-[40px] font-black leading-[1.1] tracking-tight">
                Tauche in die<br/>
                Welt der<br/>
                Sportwetten ein!
              </h2>
            </div>
          </div>

          {/* Right Side: Login Form */}
          <div className="w-full md:w-[55%] p-10 flex flex-col justify-center relative">
            {/* Close Button */}
            <button 
              onClick={onClose}
              className="absolute top-6 right-6 text-gray-400 hover:text-gray-900 transition"
            >
              <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>

            <h1 className="text-3xl font-black text-gray-900 mb-2 tracking-tight">Einloggen</h1>
            <p className="text-[13px] text-gray-500 mb-8 font-medium">
              Noch kein Konto?{' '}
              <button 
                type="button" 
                onClick={onRegisterClick}
                className="text-[#0059b3] font-bold hover:underline"
              >
                Jetzt registrieren
              </button>
            </p>

            <form onSubmit={handleSubmit} className="flex flex-col gap-6">
              
              {/* Identity (Email/Username) */}
              <div className="flex flex-col gap-1.5">
                <label className="text-[11px] font-black text-gray-400 uppercase tracking-widest">E-Mail / Benutzername *</label>
                <input
                  type="text"
                  value={identity}
                  onChange={(e) => setIdentity(e.target.value)}
                  className="bg-[#f2f2f2] border-none rounded-md px-4 py-3 text-sm font-bold text-gray-900 focus:ring-2 focus:ring-blue-500 transition-shadow outline-none"
                  required
                />
              </div>

              {/* Password */}
              <div className="flex flex-col gap-1.5">
                <label className="text-[11px] font-black text-gray-400 uppercase tracking-widest">Passwort *</label>
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="w-full bg-[#f2f2f2] border-none rounded-md px-4 py-3 text-sm font-bold text-gray-900 focus:ring-2 focus:ring-blue-500 transition-shadow outline-none pr-12"
                    required
                  />
                  <button 
                    type="button" 
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-900"
                  >
                    {showPassword ? (
                      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.542-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88L6.59 6.59m7.532 7.532l3.29 3.29M3 3l18 18" /></svg>
                    ) : (
                      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" /></svg>
                    )}
                  </button>
                </div>
              </div>

              {/* Remember Me & Forgot Password */}
              <div className="flex items-center justify-between">
                <label className="flex items-center gap-2 cursor-pointer group">
                  <div className="relative">
                    <input 
                      type="checkbox" 
                      className="peer sr-only" 
                      checked={rememberMe}
                      onChange={(e) => setRememberMe(e.target.checked)}
                    />
                    <div className="w-5 h-5 border-2 border-gray-300 rounded group-hover:border-gray-400 transition-colors peer-checked:bg-black peer-checked:border-black flex items-center justify-center">
                      <svg className="w-3.5 h-3.5 text-white opacity-0 peer-checked:opacity-100 transition-opacity" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" /></svg>
                    </div>
                  </div>
                  <span className="text-[13px] font-bold text-gray-700">Login merken</span>
                </label>
                <button type="button" className="text-[13px] font-bold text-[#0059b3] hover:underline">
                  Passwort vergessen?
                </button>
              </div>

              {/* Error */}
              {error && <p className="text-tipico-red text-[13px] font-bold">{error}</p>}

              {/* Submit Button */}
              <button
                type="submit"
                disabled={loading}
                className="w-full bg-[#212529] text-white font-black py-4 rounded-md text-sm uppercase tracking-widest hover:bg-gray-800 transition shadow-lg active:scale-[0.98] disabled:opacity-50 mt-2"
              >
                {loading ? 'Bitte warten...' : 'Einloggen'}
              </button>

              {/* Activation Link */}
              <p className="text-center text-[13px] font-bold text-gray-500 mt-4">
                Dükkan müşterisi misiniz?{' '}
                <button type="button" className="text-[#0059b3] hover:underline">
                  Online hesabı aktifleştir
                </button>
              </p>
            </form>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="w-full max-w-[960px] mx-auto py-8 px-4 text-[11px] text-gray-400 leading-relaxed">
        <p className="text-center">
          Tipico ist ein offiziell lizenzierter Sportwettenveranstalter. <br/>
          Glücksspiel kann süchtig machen. Spiele verantwortungsbewusst. 18+
        </p>
      </footer>
    </div>
  );
};

export default DesktopLoginPage;
